import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MovieFilterIcon from '@mui/icons-material/MovieFilter';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ReplayIcon from '@mui/icons-material/Replay';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

import { api, ApiError } from '../api/client';
import { apiErrorText } from '../api/errorText';
import type {
  CatalogueEpisode,
  CatalogueTitleRef,
  CatalogueVersion,
  DiscoveryKind,
  ItemPerson,
  LibraryItem,
  MediaSourceSummary,
  PlaybackProgress,
} from '../api/types';
import { useApi } from '../api/useApi';
import { useAuth } from '../auth/AuthContext';
import AsyncSection from '../components/AsyncSection';
import { usePageTitle } from '../components/PageTitle';
import CastRow from '../components/media/CastRow';
import CatalogueDialog from '../components/media/CatalogueDialog';
import ItemVersions from '../components/media/ItemVersions';
import VersionChoiceDialog from '../components/media/VersionChoiceDialog';
import VersionPicker from '../components/media/VersionPicker';
import {
  embedUrl,
  episodeCode,
  formatMinutes,
  formatRemaining,
  toSeconds,
} from '../components/media/playbackTime';
import { useDominantColour } from '../components/media/useDominantColour';
import { ResumeProgress, TileProgress } from '../components/media/WatchProgress';
import PlayerOverlay from '../components/player/PlayerOverlay';
import { barOffset, groundRgb, shellStart } from '../theme';

/// What went wrong while making a title playable: the translated sentence, and the server's own
/// words where they say more - which title it looked for, and what it tried.
interface PrepareProblem {
  message: string;
  remedy?: string;
  detail?: string;
}

/// Which season and episode something is, where it is one.
interface EpisodeNumber {
  seasonNumber: number;
  episodeNumber: number;
}

/// One title of the catalogue, drawn as though the server held it - because where everything
/// plays on demand, it as good as does.
///
/// Everything on the page comes straight from the catalogue, so it is there the moment the page
/// is: nothing waits for a scan or for metadata to be looked up. Pressing play is the one moment
/// the server may have to fetch a version first, and the page says so while it does.
export default function CatalogueTitlePage() {
  const { provider = '', kind: kindParameter = '', id = '' } = useParams();
  const { t } = useTranslation();
  const { can } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const kind: DiscoveryKind = kindParameter === 'Series' ? 'Series' : 'Movie';
  const ref: CatalogueTitleRef = { provider, kind, id };

  const dark = theme.palette.mode === 'dark';
  const ground = dark ? groundRgb.dark : groundRgb.light;

  // The scrim runs from the edge the title stands on, which is the right one on an Arabic
  // screen: a wash on the far side would darken the picture and leave the letters on the bare
  // still.
  const toTitle = theme.direction === 'rtl' ? 270 : 90;

  const details = useApi(['discovery', 'title', provider, kind, id], () => api.catalogueTitle(ref), {
    staleTime: 5 * 60 * 1000,
  });

  const shown = details.data;
  const heldId = shown?.libraryItemId ?? undefined;

  /// What this person has watched, which decides Resume and the bars under the episodes.
  const progress = useApi(['playback', 'progress'], api.playbackProgress);

  /// The held item and its files, for a film this server already has.
  const heldItem = useApi(['items', heldId ?? ''], () => api.item(heldId ?? ''), {
    enabled: Boolean(heldId) && kind === 'Movie',
  });
  const versions = useApi(['items', heldId ?? '', 'versions'], () => api.itemVersions(heldId ?? ''), {
    enabled: Boolean(heldId) && kind === 'Movie',
  });
  const discoveryProviders = useApi(['discovery', 'providers'], api.discoveryProviders, {
    enabled: can('Discover'),
  });

  // Specials last, which is where a person looks for them: season one is the start of a programme,
  // and "season 0" in front of it is a catalogue's numbering, not an order anybody watches in.
  const seasons = [...(shown?.seasons ?? [])].sort(
    (a, b) => (a.seasonNumber === 0 ? 1 : 0) - (b.seasonNumber === 0 ? 1 : 0) || a.seasonNumber - b.seasonNumber,
  );

  const [chosenSeason, setChosenSeason] = useState<number>();
  const seasonNumber = chosenSeason ?? seasons[0]?.seasonNumber;

  const season = useApi(
    ['discovery', 'title', provider, kind, id, 'season', seasonNumber ?? -1],
    () => api.catalogueSeason(ref, seasonNumber ?? 0),
    { enabled: kind === 'Series' && seasonNumber !== undefined, staleTime: 5 * 60 * 1000 },
  );

  const [version, setVersion] = useState<string>();
  const [trailerOpen, setTrailerOpen] = useState(false);
  const [fetchingAnother, setFetchingAnother] = useState(false);

  /// Which play somebody is choosing a version for, where they asked to be asked. Undefined
  /// while nothing is being chosen, which is what keeps the versions from being looked up at all.
  const [asking, setAsking] = useState<{ episode?: EpisodeNumber; fromStart: boolean }>();

  /// What this person wants to happen when a version has to be fetched. A server that does not
  /// know the setting, or a person who never chose, gets the server's own pick.
  const preferences = useApi(['users', 'me', 'preferences'], api.myPreferences, {
    staleTime: 5 * 60 * 1000,
  });

  // Spelled as the server spells it, and read as it comes: a server that names the setting
  // differently in some later version must not silently turn the choice off.
  const asksForVersion = (preferences.data?.versionChoice ?? 'Automatic').toLowerCase() === 'ask';

  const choices = useApi(
    [
      'discovery', 'title', provider, kind, id, 'versions',
      asking?.episode?.seasonNumber ?? -1,
      asking?.episode?.episodeNumber ?? -1,
    ],
    () => api.catalogueTitleVersions(ref, asking?.episode),
    { enabled: asking !== undefined },
  );

  /// Which play is being prepared: `title` for a film, `S:E` for an episode.
  const [preparing, setPreparing] = useState<string>();
  const [problem, setProblem] = useState<PrepareProblem>();
  const [playing, setPlaying] = useState<{ item: LibraryItem; from: number; mediaSourceId?: string }>();

  usePageTitle(shown?.title.title);

  const poster = shown?.title.posterUrl ?? undefined;
  const backdrop = shown?.title.backdropUrl ?? undefined;
  const logo = backdrop ? shown?.logoUrl ?? undefined : undefined;
  const tint = useDominantColour(poster ?? backdrop);

  const episodes = season.data?.episodes ?? [];

  const seenOf = (itemId: string | null | undefined): PlaybackProgress | undefined =>
    itemId ? progress.data?.[itemId] : undefined;

  /// Where to carry on in the season on screen: the episode that was started and not finished,
  /// otherwise the first one not seen, otherwise the first.
  const nextEpisode = (() => {
    if (episodes.length === 0) {
      return undefined;
    }

    const started = episodes.find((episode) => {
      const seen = seenOf(episode.libraryItemId);

      return seen && !seen.isWatched && seen.fraction > 0;
    });

    return started ?? episodes.find((episode) => !seenOf(episode.libraryItemId)?.isWatched) ?? episodes[0];
  })();

  const heldProgress = seenOf(heldId);
  const resumeSeconds = heldProgress && !heldProgress.isWatched ? toSeconds(heldProgress.position) : 0;

  /// Opens the player on something this server holds.
  const startPlayer = async (itemId: string, mediaSourceId: string | undefined, fromStart: boolean) => {
    const item = await api.item(itemId);
    const seen = fromStart ? undefined : progress.data?.[item.id];

    setPlaying({
      item,
      from: seen && !seen.isWatched ? toSeconds(seen.position) : 0,
      mediaSourceId,
    });
  };

  /// Plays the film, or one episode: straight away where this server holds it, otherwise after the
  /// server has fetched a version for it - which is the wait the button shows.
  ///
  /// Where somebody asked to pick the version themselves, the first press opens the list instead
  /// and the second one arrives here with the version they picked.
  const play = async (
    episode?: EpisodeNumber & { libraryItemId?: string | null; mediaSourceId?: string | null },
    fromStart = false,
    releaseId?: string,
  ) => {
    const held = episode
      ? Boolean(episode.libraryItemId && episode.mediaSourceId)
      : Boolean(heldId && heldItem.data?.isPlayable);

    if (!held && asksForVersion && releaseId === undefined) {
      setProblem(undefined);
      setAsking({
        episode: episode ? { seasonNumber: episode.seasonNumber, episodeNumber: episode.episodeNumber } : undefined,
        fromStart,
      });

      return;
    }

    const key = episode ? `${episode.seasonNumber}:${episode.episodeNumber}` : 'title';

    setPreparing(key);
    setProblem(undefined);

    try {
      if (!episode && heldId && heldItem.data?.isPlayable) {
        await startPlayer(heldId, version, fromStart);

        return;
      }

      if (episode?.libraryItemId && episode.mediaSourceId) {
        await startPlayer(episode.libraryItemId, episode.mediaSourceId, fromStart);

        return;
      }

      const result = await api.playOnDemand(
        ref,
        episode ? { seasonNumber: episode.seasonNumber, episodeNumber: episode.episodeNumber } : undefined,
        releaseId,
      );

      if (result.fetched) {
        // Something arrived on the shelf: the tile, the rows and this page all hold it now.
        void queryClient.invalidateQueries({ queryKey: ['items'] });
        void queryClient.invalidateQueries({ queryKey: ['discovery'] });
      }

      await startPlayer(result.libraryItemId, result.mediaSourceId, fromStart);
    } catch (caught) {
      const text = apiErrorText(caught, t);
      const detail = caught instanceof ApiError && caught.message !== text.message ? caught.message : undefined;

      setProblem({ ...text, detail });
    } finally {
      setPreparing(undefined);
    }
  };

  const removableBy = new Set((discoveryProviders.data ?? []).map((candidate) => candidate.providerId));
  const canFetchAnother = can('Discover') && Boolean(shown?.canFetch)
    && (discoveryProviders.data ?? []).some((candidate) => candidate.isReady);

  /// Takes one version off the shelf. Where it was the last, the title stays on this page - it is
  /// the catalogue's - and simply plays on demand again next time.
  const removeVersion = async (source: MediaSourceSummary) => {
    if (!source.reference) {
      return;
    }

    await api.removeRelease(source.reference.providerId, source.reference.key);

    if (version === source.id) {
      setVersion(undefined);
    }

    void queryClient.invalidateQueries({ queryKey: ['items'] });
    void queryClient.invalidateQueries({ queryKey: ['discovery'] });
  };

  const playable = Boolean(heldId) || Boolean(shown?.playsOnDemand);

  const playLabel = (() => {
    if (kind === 'Movie') {
      return resumeSeconds > 0 ? t('item.resume') : t('item.play');
    }

    if (!nextEpisode) {
      return t('item.play');
    }

    const code = episodeCode(nextEpisode.seasonNumber, nextEpisode.episodeNumber);
    const seen = seenOf(nextEpisode.libraryItemId);

    return seen && seen.fraction > 0 && !seen.isWatched
      ? t('item.resumeEpisode', { episode: code })
      : t('item.playEpisode', { episode: code });
  })();

  const people: ItemPerson[] = (shown?.people ?? []).map((person) => ({
    name: person.name,
    role: person.role ?? null,
    kind: person.kind,
    providerKey: person.providerKey ?? null,
    personId: person.personId ?? null,
  }));

  return (
    <AsyncSection loading={details.loading} error={details.error} onRetry={details.reload}>
      {shown && (
        <Box
          sx={{
            // Out to the window's edges, under the sidebar's glass and the bar, as a held title's.
            marginInlineStart: { xs: -2, md: `calc(-32px - ${shellStart})` },
            marginInlineEnd: { xs: -2, md: -4 },
            mt: { xs: `calc(-16px - ${barOffset.xs}px)`, md: `calc(-24px - ${barOffset.md}px)` },
            background: tint ? `linear-gradient(180deg, ${tint} 0%, transparent 70%)` : undefined,
          }}
        >
          <Box
            sx={{
              position: 'relative',
              minHeight: { xs: 260 + barOffset.xs, md: 480 + barOffset.md },
              display: 'flex',
              alignItems: 'flex-end',
              paddingInlineStart: { xs: 2, md: `calc(48px + ${shellStart})` },
              paddingInlineEnd: { xs: 2, md: 6 },
              pt: { xs: `${48 + barOffset.xs}px`, md: `${80 + barOffset.md}px` },
              pb: { xs: 3, md: 5 },
              backgroundImage: backdrop ? `url(${backdrop})` : undefined,
              backgroundSize: 'cover',
              backgroundPosition: 'center 20%',
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background: backdrop
                  ? `linear-gradient(0deg, rgba(${ground},0.96) 0%, rgba(${ground},0.55) 45%,`
                    + ` rgba(${ground},0.15) 100%),`
                    + ` linear-gradient(${toTitle}deg, rgba(${ground},0.85) 0%, rgba(${ground},0.1) 70%)`
                  : 'none',
                pointerEvents: 'none',
              },
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 2, md: 4 }}
              sx={{ position: 'relative', zIndex: 1, alignItems: 'flex-end', width: '100%' }}
            >
              {poster && (
                <Box
                  component="img"
                  src={poster}
                  alt=""
                  sx={{ width: { xs: 110, md: 200 }, borderRadius: 2, boxShadow: 10, display: 'block', flexShrink: 0 }}
                />
              )}

              <Stack spacing={1.5} sx={{ minWidth: 0, flexGrow: 1 }}>
                {logo ? (
                  <Box
                    component="img"
                    src={logo}
                    alt={shown.title.title}
                    sx={{
                      alignSelf: 'flex-start',
                      maxWidth: { xs: 220, sm: 300, md: 420 },
                      maxHeight: { xs: 72, md: 132 },
                      objectFit: 'contain',
                      objectPosition: theme.direction === 'rtl' ? 'right bottom' : 'left bottom',
                      display: 'block',
                      filter: dark
                        ? 'drop-shadow(0 2px 24px rgba(0,0,0,0.6))'
                        : 'drop-shadow(0 1px 2px rgba(0,0,0,0.55)) drop-shadow(0 2px 18px rgba(0,0,0,0.35))',
                    }}
                  />
                ) : (
                  <Typography
                    variant="h1"
                    sx={{
                      textShadow: backdrop
                        ? `0 2px 24px rgba(${dark ? '0,0,0' : '255,255,255'},${dark ? 0.6 : 0.85})`
                        : undefined,
                    }}
                  >
                    {shown.title.title}
                  </Typography>
                )}

                {shown.tagline && (
                  <Typography sx={{ fontStyle: 'italic' }} color="text.secondary">
                    {shown.tagline}
                  </Typography>
                )}

                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }} useFlexGap>
                  <Chip size="small" label={t(`itemKind.${kind}`)} />
                  {shown.title.year !== undefined && <Chip size="small" variant="outlined" label={shown.title.year} />}
                  {shown.runtimeMinutes ? (
                    <Chip size="small" variant="outlined" label={formatMinutes(shown.runtimeMinutes)} />
                  ) : null}
                  {shown.officialRating && (
                    <Chip size="small" variant="outlined" label={shown.officialRating} sx={{ fontWeight: 700, borderWidth: 2 }} />
                  )}
                  {shown.title.rating ? (
                    <Chip size="small" variant="outlined" label={`★ ${shown.title.rating.toFixed(1)}`} />
                  ) : null}
                  {shown.genres.map((genre) => (
                    <Chip key={genre} size="small" label={genre} />
                  ))}
                </Stack>

                <Stack spacing={1.5} sx={{ pt: 1, maxWidth: 560 }}>
                  <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center' }} useFlexGap>
                    {playable && (kind === 'Movie' || nextEpisode) && (
                      <Button
                        variant="contained"
                        size="large"
                        disabled={preparing !== undefined}
                        startIcon={
                          preparing === 'title'
                          || (nextEpisode && preparing === `${nextEpisode.seasonNumber}:${nextEpisode.episodeNumber}`)
                            ? <CircularProgress size={22} color="inherit" />
                            : <PlayArrowIcon sx={{ fontSize: '2rem !important' }} />
                        }
                        onClick={() => void (kind === 'Movie' ? play() : play(nextEpisode))}
                        sx={{ px: 4, py: 1.5, fontSize: '1.05rem', fontWeight: 700, borderRadius: 2 }}
                      >
                        {preparing === 'title'
                        || (nextEpisode && preparing === `${nextEpisode.seasonNumber}:${nextEpisode.episodeNumber}`)
                          ? t('title.preparing')
                          : playLabel}
                      </Button>
                    )}

                    {kind === 'Movie' && heldItem.data?.isPlayable && (
                      <VersionPicker versions={versions.data ?? []} value={version} onChange={setVersion} />
                    )}

                    {kind === 'Movie' && resumeSeconds > 0 && (
                      <Button
                        variant="text"
                        size="large"
                        startIcon={<ReplayIcon />}
                        disabled={preparing !== undefined}
                        onClick={() => {
                          if (heldId) {
                            void api.clearProgress(heldId).then(() => progress.reload());
                          }

                          void play(undefined, true);
                        }}
                      >
                        {t('item.playFromStart')}
                      </Button>
                    )}

                    {shown.trailerUrl && (
                      <Button
                        variant="outlined"
                        size="large"
                        startIcon={<MovieFilterIcon />}
                        onClick={() => setTrailerOpen((open) => !open)}
                      >
                        {t('item.trailer')}
                      </Button>
                    )}
                  </Stack>

                  {preparing !== undefined && (
                    // Said once, while it happens: the first play of something is the one that
                    // waits, and a button that just spins does not say why.
                    <Typography variant="body2" color="text.secondary">
                      {t('title.preparingHint')}
                    </Typography>
                  )}

                  {kind === 'Movie' && heldProgress && heldProgress.fraction > 0 && !heldProgress.isWatched && (
                    <Stack spacing={0.5}>
                      <ResumeProgress fraction={heldProgress.fraction} />

                      <Typography variant="caption" color="text.secondary">
                        {t('item.remaining', { time: formatRemaining(heldProgress.position, heldProgress.duration) })}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Stack>
            </Stack>
          </Box>

          <Stack
            spacing={4}
            sx={{
              paddingInlineStart: { xs: 2, md: `calc(48px + ${shellStart})` },
              paddingInlineEnd: { xs: 2, md: 6 },
              pt: { xs: 3, md: 5 },
              pb: 8,
            }}
          >
            {shown.title.overview && <Typography sx={{ maxWidth: '70ch' }}>{shown.title.overview}</Typography>}

            {problem && (
              <Alert severity="warning" onClose={() => setProblem(undefined)}>
                <AlertTitle>{t('title.cannotPrepare')}</AlertTitle>
                <div>{problem.message}</div>
                {problem.remedy && <div>{problem.remedy}</div>}
                {problem.detail && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {problem.detail}
                  </Typography>
                )}
              </Alert>
            )}

            {!playable && (
              <Typography color="text.secondary">{t('discover.nothingCanFetch')}</Typography>
            )}

            {trailerOpen && shown.trailerUrl && (
              <Stack spacing={1} sx={{ maxWidth: 960 }}>
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    aspectRatio: '16 / 9',
                    borderRadius: 3,
                    overflow: 'hidden',
                    backgroundColor: 'black',
                  }}
                >
                  <Box
                    component="iframe"
                    src={embedUrl(shown.trailerUrl)}
                    title={t('item.trailer')}
                    allow="accelerometer; autoplay; encrypted-media; picture-in-picture"
                    allowFullScreen
                    sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
                  />
                </Box>

                <Typography variant="caption" color="text.secondary">
                  {t('item.trailerSource')}
                </Typography>
              </Stack>
            )}

            {kind === 'Series' && seasons.length > 0 && (
              <Stack spacing={2}>
                <Typography variant="h3">{t('item.episodes')}</Typography>

                <Tabs
                  value={seasonNumber ?? false}
                  onChange={(_, next: number) => setChosenSeason(next)}
                  variant="scrollable"
                  allowScrollButtonsMobile
                >
                  {seasons.map((candidate) => (
                    <Tab
                      key={candidate.seasonNumber}
                      value={candidate.seasonNumber}
                      label={candidate.name || t('title.season', { number: candidate.seasonNumber })}
                    />
                  ))}
                </Tabs>

                <AsyncSection loading={season.loading} error={season.error} onRetry={season.reload}>
                  <Stack spacing={1} sx={{ maxWidth: 1100 }}>
                    {episodes.map((episode) => (
                      <EpisodeRow
                        key={`${episode.seasonNumber}:${episode.episodeNumber}`}
                        episode={episode}
                        seen={seenOf(episode.libraryItemId)}
                        playable={playable || Boolean(episode.mediaSourceId)}
                        preparing={preparing === `${episode.seasonNumber}:${episode.episodeNumber}`}
                        busy={preparing !== undefined}
                        onPlay={() => void play(episode)}
                        onOpen={episode.libraryItemId ? () => void navigate(`/items/${episode.libraryItemId}`) : undefined}
                      />
                    ))}
                  </Stack>
                </AsyncSection>
              </Stack>
            )}

            {people.length > 0 && <CastRow people={people} />}

            {kind === 'Movie' && heldId && (versions.data?.length ?? 0) > 0 && (
              <ItemVersions
                versions={versions.data ?? []}
                removableBy={removableBy}
                canRemove={can('ManageLibraries')}
                canFetchAnother={canFetchAnother}
                onRemove={removeVersion}
                onFetchAnother={() => setFetchingAnother(true)}
              />
            )}
          </Stack>

          {asking && (
            <VersionChoiceDialog
              title={shown.title.title}
              versions={choices.data?.versions}
              loading={choices.loading}
              error={choices.error}
              onReload={choices.reload}
              onClose={() => setAsking(undefined)}
              onPick={(picked: CatalogueVersion) => {
                const chosen = asking;

                setAsking(undefined);
                void play(chosen.episode, chosen.fromStart, picked.id);
              }}
            />
          )}

          <CatalogueDialog
            entry={
              fetchingAnother
                ? { title: shown.title, libraryItemId: heldId, canFetch: shown.canFetch }
                : undefined
            }
            lookOnOpen
            onClose={() => setFetchingAnother(false)}
            onFetched={() => {
              details.reload();
              versions.reload();
            }}
          />

          {playing && (
            <PlayerOverlay
              target={{ kind: 'item', item: playing.item, startAt: playing.from, mediaSourceId: playing.mediaSourceId }}
              onClose={(saved) => {
                setPlaying(undefined);

                // Once the player's last position has arrived, or this reads the one before it.
                void saved.then(() => progress.reload());
              }}
            />
          )}
        </Box>
      )}
    </AsyncSection>
  );
}

/// One episode of the season on screen: the still, what it is, how far in somebody got, and a
/// button that plays it - fetching it first where the server holds none of it.
function EpisodeRow({
  episode,
  seen,
  playable,
  preparing,
  busy,
  onPlay,
  onOpen,
}: {
  episode: CatalogueEpisode;
  seen: PlaybackProgress | undefined;
  playable: boolean;
  preparing: boolean;
  busy: boolean;
  onPlay: () => void;
  onOpen?: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Stack
      direction="row"
      spacing={2}
      onClick={playable && !busy ? onPlay : undefined}
      sx={{
        p: 1.5,
        borderRadius: 2,
        alignItems: 'center',
        cursor: playable && !busy ? 'pointer' : 'default',
        transition: 'background-color 160ms ease',
        '&:hover': { backgroundColor: playable ? 'action.hover' : undefined },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: { xs: 120, sm: 160 },
          aspectRatio: '16 / 9',
          borderRadius: 1.5,
          overflow: 'hidden',
          flexShrink: 0,
          backgroundColor: 'background.paper',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        {episode.stillUrl ? (
          <Box
            component="img"
            src={episode.stillUrl}
            alt=""
            loading="lazy"
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Typography sx={{ fontSize: 28, fontWeight: 700, opacity: 0.4 }}>{episode.episodeNumber}</Typography>
        )}

        {seen && seen.fraction > 0 && !seen.isWatched && <TileProgress fraction={seen.fraction} />}
      </Box>

      <Stack spacing={0.5} sx={{ minWidth: 0, flexGrow: 1 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Typography sx={{ fontWeight: 600 }} noWrap>
            {episode.episodeNumber}. {episode.name}
          </Typography>

          {seen?.isWatched && <CheckCircleIcon fontSize="small" color="success" titleAccess={t('item.watched')} />}
        </Stack>

        {episode.runtimeMinutes ? (
          <Typography variant="caption" color="text.secondary">
            {formatMinutes(episode.runtimeMinutes)}
          </Typography>
        ) : null}

        {episode.overview && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
          >
            {episode.overview}
          </Typography>
        )}
      </Stack>

      {onOpen && (
        <IconButton
          aria-label={t('title.episodeDetails')}
          onClick={(event) => {
            event.stopPropagation();
            onOpen();
          }}
        >
          <InfoOutlinedIcon />
        </IconButton>
      )}

      {playable && (
        <IconButton
          aria-label={t('item.play')}
          disabled={busy}
          onClick={(event) => {
            event.stopPropagation();
            onPlay();
          }}
          sx={{ width: 48, height: 48 }}
        >
          {preparing ? <CircularProgress size={22} /> : <PlayArrowIcon />}
        </IconButton>
      )}
    </Stack>
  );
}
